// Amadeus upstream calls — service_handler wrappers only. Every function
// returns either a Promise resolving to a normalized FlightOffer[] / catalog
// / raw service response. Raw envelopes never leak out of this module:
// responses are either normalized through `../normalize` or consumed
// directly by the caller for its own parsing (seatmaps, upsell).
//
// Each function takes the provider instance as its first argument so side
// effects (dictionary cache, extra-bags stash) land on the right instance.

import { service_handler } from "@/services";
import { deep_clone } from "@/utilities/";
import { apply_markup_to_number } from "@liveflight/utilities/price";
import { can_all_passengers_select_one_seat } from "@liveflight/utilities/seat-map";
import { normalize, extract_offers_array } from "./normalize";
import { merge_dictionaries } from "../base/helpers";
import { extract_dictionaries } from "./dictionaries";
import { build_mapped_decks, validate_traveler_pricing } from "./seats";
import { build_book_body, build_search_request } from "./request";

/**
 * Parse a pre-fetched Amadeus search response into normalized
 * FlightOffer[]. Used by `search` after service_handler resolves and
 * exposed for tests / offline fixtures.
 *
 * @param {Provider} provider
 * @param {Object} response
 * @returns {Object[]}
 */
export const parse_search_response = (provider, response) => {
  merge_dictionaries(provider, extract_dictionaries(response));
  return extract_offers_array(response).map((raw_offer) =>
    normalize(provider, raw_offer),
  );
};

/**
 * Generic search entry point. Amadeus is the default provider so the
 * service key is unprefixed.
 *
 * @param {Provider} provider
 * @param {Object} payload generic search request body
 * @returns {Promise<Object[]>}
 */
export const search = (provider, payload) =>
  service_handler("flightOffers", build_search_request(payload)).then(
    (response) => parse_search_response(provider, response),
  );

/**
 * Amadeus no-results suggestions. Fans out two variants of the original
 * search payload — wider origin/destination radius, and a ±3-day window
 * around each date — and returns their normalized offers in separate
 * buckets. The UI uses the radius bucket to suggest nearby airports and
 * the time-window bucket to suggest neighbouring dates.
 *
 * Suggestion search is gated on payload shape: Amadeus only supports the
 * widened-search variants for one-way / round-trip itineraries
 * (originDestinations.length 1 or 2). Multi-itinerary requests resolve
 * to empty buckets — there's no documented widening behaviour for them.
 *
 * Both variants are issued in parallel; a single variant failing must not
 * empty the other bucket — Promise.allSettled lets each settle on its own.
 *
 * @param {Provider} provider
 * @param {Object} payload generic search request body
 * @returns {Promise<{ by_radius: Object[], by_time_window: Object[] }>}
 */
export const fetch_search_suggestion = (provider, payload) => {
  const empty = { by_radius: [], by_time_window: [] };
  const { originDestinations = [] } = payload || {};
  if (originDestinations.length === 0 || originDestinations.length > 2) {
    return Promise.resolve(empty);
  }

  const RADIUS_WIDEN_KM = 100;
  const request_by_radius = deep_clone(payload);
  request_by_radius.originDestinations.forEach((o) => {
    o.originRadius = Math.max(Number(o.originRadius) || 0, RADIUS_WIDEN_KM);
    o.destinationRadius = Math.max(Number(o.destinationRadius) || 0, RADIUS_WIDEN_KM);
  });
  const { searchCriteria: radius_criteria = {} } = request_by_radius;
  if (radius_criteria.flightFilters) {
    radius_criteria.flightFilters.returnToDepartureAirport = false;
  }

  const request_by_time_window = deep_clone(payload);
  request_by_time_window.originDestinations.forEach((o) => {
    o.departureDateTimeRange = o.departureDateTimeRange || {};
    o.departureDateTimeRange.dateWindow = "I3D";
  });

  return Promise.allSettled([
    search(provider, request_by_radius),
    search(provider, request_by_time_window),
  ]).then((settled) => ({
    by_radius:
      settled[0].status === "fulfilled" && Array.isArray(settled[0].value)
        ? settled[0].value
        : [],
    by_time_window:
      settled[1].status === "fulfilled" && Array.isArray(settled[1].value)
        ? settled[1].value
        : [],
  }));
};

/**
 * flightOffersPrice returns a repriced offer + envelope-level data
 * (bookingRequirements, dictionaries, included.bags). Caches the
 * extra-bags catalog on the provider so fetch_extra_bags can serve it
 * without a second round-trip.
 *
 * @param {Provider} provider
 * @param {Object} flight_offer
 * @returns {Promise<Object>}
 */
export const get_offer_details = (provider, flight_offer) => {
  const body = {
    priceFlightOffersBody: {
      data: {
        type: "flight-offers-pricing",
        flightOffers: [flight_offer._raw],
      },
    },
    include: ["bags"],
  };
  return service_handler("flightOffersPrice", body).then((response) => {
    const { data: envelope = {} } = response || {};
    const { included = {}, data: priced_data = {} } = envelope;
    const { flightOffers = [] } = priced_data;
    merge_dictionaries(provider, extract_dictionaries(response));
    provider._extra_bags_catalog = included.bags || null;
    const priced = flightOffers[0];
    if (!priced) {
      throw new Error(
        "[AmadeusProvider] get_offer_details: reprice returned no flightOffers",
      );
    }
    return normalize(provider, priced);
  });
};

/**
 * Fetch the Amadeus seatmap and return a fully-normalized response.
 * Validation, per-deck mapping, and per-deck disabled-gating all happen
 * here so seat-selection.vue only reads pre-built data.
 *
 * @param {Provider} _provider
 * @param {Object} flight_offer
 * @param {{ areInfantsPresent?: boolean, allowMalformed?: boolean }} [options={}]
 * @returns {Promise<Object|null>}
 */
export const fetch_seatmap = (_provider, flight_offer, options = {}) =>
  service_handler("seatmaps", { flightOffer: flight_offer._raw }).then(
    (response) => {
      const { data: envelope = {} } = response || {};
      const { seatmaps = null } = envelope;
      if (!seatmaps) return null;
      const seatmapData = seatmaps.data || [];
      const isValid = validate_traveler_pricing(flight_offer, seatmapData);
      return {
        data: { seatmaps },
        dictionaries: seatmaps.dictionaries || {},
        empty: !seatmapData.length,
        isValid,
        canAllPassengersSelectOneSeat:
          can_all_passengers_select_one_seat(seatmapData),
        mappedDecks: isValid
          ? build_mapped_decks(flight_offer, seatmaps, options)
          : {},
      };
    },
  );

/**
 * Amadeus ships the extra-bags catalog in the same flightOffersPrice
 * response that get_offer_details already consumes (`include=['bags']`).
 * get_offer_details caches it on the instance so this resolves without
 * an upstream call. See providers/types.js::ExtraBag.
 *
 * @param {Provider} provider
 * @param {Object} flight_offer
 * @returns {Promise<Object|null>} canonical ExtraBagCatalog
 */
export const fetch_extra_bags = (provider, flight_offer) => {
  const raw_catalog = provider._extra_bags_catalog;
  if (!raw_catalog) return Promise.resolve(null);
  const { price: offer_price = {} } = flight_offer || {};
  const catalog = {};
  Object.keys(raw_catalog).forEach((bag_id) => {
    const raw = raw_catalog[bag_id];
    const { price: raw_price = {} } = raw;
    const raw_amount = raw_price.amount;
    const total_number = Number(raw_amount || 0);
    // Preserve trailing zeros — `String(Number("12.50"))` drops them.
    let total = String(total_number);
    if (typeof raw_amount === "string" && raw_amount.length > 0) {
      total = raw_amount;
    }
    const grandTotal = String(
      apply_markup_to_number(flight_offer, total_number),
    );
    catalog[bag_id] = {
      id: raw.id,
      name: raw.name,
      quantity: raw.quantity,
      weight: raw.weight,
      weightUnit: raw.weightUnit,
      travelerIds: raw.travelerIds || [],
      segmentIds: raw.segmentIds || [],
      bookableByItinerary: !!raw.bookableByItinerary,
      price: {
        total,
        grandTotal,
        base: "",
        taxes: [],
        currency: offer_price.currency || raw_price.currency || "",
        currencySymbol: offer_price.currencySymbol || "",
      },
      _provider: provider.providerId,
      _raw: raw,
    };
  });
  return Promise.resolve(catalog);
};

/**
 * Translate the canonical CheckoutRequest into Amadeus's flight-order
 * wire body and POST it. The wire shape lives in ./request.js so its
 * concerns stay in one place. Amadeus /orders rejects anything that
 * isn't the original raw flight-offer shape, so build_book_body swaps
 * each entry in flightOffers to its _raw twin before hitting the wire.
 *
 * @param {Provider} _provider
 * @param {Object} flight_offer
 * @param {Object} request canonical CheckoutRequest
 * @returns {Promise<Object>}
 */
export const book = (_provider, flight_offer, request) =>
  service_handler("book", build_book_body(flight_offer, request), { track: 'checkout' });

/**
 * Surface the canonical confirmation shape from a /confirm response.
 * `voucher`. Offer entries are normalized canonical
 * FlightOffers; travelers pass through as-is.
 *
 * @param {Provider} provider
 * @param {Object} body parsed /confirm response body
 * @returns {Object}
 */
export const get_confirmation = (provider, body) => {
  const safe_body = body || {};
  const { flightVoucher: raw_voucher = {}, ...rest } = safe_body;
  const { orderData = {}, ...voucher_root } = raw_voucher;
  const { data = {} } = orderData;
  merge_dictionaries(provider, extract_dictionaries(safe_body));
  const raw_offers = Array.isArray(data.flightOffers) ? data.flightOffers : [];
  const flight_offers = raw_offers.map((raw) => normalize(provider, raw));
  return {
    ...rest,
    voucher: {
      ...voucher_root,
      flight_offers,
      travelers: Array.isArray(data.travelers) ? data.travelers : [],
      tickets: Array.isArray(data.tickets) ? data.tickets : [],
    },
  };
};
